import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Download, FileText, Calendar, User, Building2, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { downloadFile, blobErrorDetail } from '../lib/downloadFile';

export default function Forms() {
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const isAdmin = ['admin', 'hr_manager', 'payroll_officer'].includes(user.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, payslipsRes] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/payslips`)
      ]);
      setEmployees(empRes.data);
      setPayslips(payslipsRes.data);
      
      // Auto-select current employee if not admin
      if (!isAdmin) {
        const currentEmp = empRes.data.find(emp => emp.email === user.email);
        if (currentEmp) {
          setSelectedEmployee(currentEmp.employee_id);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const handleDownloadForm = async (formType) => {
    const companyLevel = formType === 'FORM_E';
    if (!companyLevel && !selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }

    setLoading(true);
    try {
      const url = companyLevel
        ? `${API}/payroll/gov-forms/form_e?year=${selectedYear}`
        : `${API}/forms/download/${formType}?employee_id=${selectedEmployee}&year=${selectedYear}`;
      const fallback = companyLevel ? `Form_E_CP8D_${selectedYear}.pdf` : `${formType}_${selectedEmployee}_${selectedYear}.pdf`;
      await downloadFile(url, fallback);
      toast.success(`${formType} downloaded successfully`);
    } catch (error) {
      const detail = await blobErrorDetail(error, '');
      if (detail && /no payroll|no payslips/i.test(detail)) {
        toast.error(`${detail}. Generate payslips in Payroll before downloading ${formType}.`);
      } else {
        toast.error(detail || `Failed to download ${formType}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Iter88d — Compute payslip availability for the selected employee /
  // tax year so the UI can show whether forms will succeed BEFORE the
  // user clicks. Reported by Ankur 09-Jun-2026: switching from Ahmad
  // (who has payroll) to Siti (who doesn't) failed with a cryptic
  // "Failed to download EA" toast.
  const hasPayslipForSelection = useMemo(() => {
    if (!selectedEmployee) return false;
    return (payslips || []).some(p =>
      p.employee_id === selectedEmployee && Number(p.year) === Number(selectedYear)
    );
  }, [payslips, selectedEmployee, selectedYear]);

  const forms = [
    {
      code: 'EA',
      title: 'Borang EA (C.P.8A)',
      description: 'Yearly statement of remuneration given to each employee for personal tax filing. Official LHDN layout, sections A–F.',
      icon: FileText,
      color: 'from-blue-500 to-cyan-500',
      required: 'Annual · by 28 Feb',
      authority: 'LHDN (Inland Revenue Board)'
    },
    {
      code: 'FORM_E',
      title: 'Borang E (C.P.8D)',
      description: 'Employer\'s yearly return of all employees\' remuneration and PCB, with the C.P.8D employee listing attached. Company-level — no employee selection needed.',
      icon: Building2,
      color: 'from-emerald-500 to-teal-500',
      required: 'Annual · by 31 Mar',
      authority: 'LHDN (Inland Revenue Board)',
      companyLevel: true
    },
    {
      code: 'PCB2_II',
      title: 'PCB 2(II)',
      description: 'Statement of Monthly Tax Deductions (MTD/PCB) and CP38 remitted for the employee, month by month.',
      icon: Calendar,
      color: 'from-purple-500 to-pink-500',
      required: 'Annual',
      authority: 'LHDN'
    },
    {
      code: 'CP22',
      title: 'CP22 — New Employee',
      description: 'Notification of a new employee (s.83(2)). Submit within 30 days of the commencement date.',
      icon: User,
      color: 'from-indigo-500 to-purple-500',
      required: 'Within 30 days of joining',
      authority: 'LHDN',
      noPayrollNeeded: true
    },
    {
      code: 'CP22A',
      title: 'CP22A — Cessation',
      description: 'Notification of cessation of employment, private sector (s.83(3)). Submit at least 30 days before the last working day.',
      icon: User,
      color: 'from-pink-500 to-rose-500',
      required: 'Upon resignation / termination',
      authority: 'LHDN'
    },
    {
      code: 'CP21',
      title: 'CP21 — Leaving Malaysia',
      description: 'Notification that an employee is leaving Malaysia for more than 3 months (s.83(4)); tax clearance letter required before final payment.',
      icon: FileText,
      color: 'from-orange-500 to-red-500',
      required: 'Upon leaving Malaysia',
      authority: 'LHDN'
    }
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Forms & Documents</h2>
        <p className="text-slate-600 mt-1">Download statutory forms and tax documents</p>
      </div>

      {/* Selection Controls */}
      <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Select Employee
            </label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              disabled={!isAdmin}
              data-testid="employee-select"
              className="w-full h-11 rounded-xl border border-slate-300 px-4 bg-white disabled:bg-slate-50"
            >
              <option value="">Choose employee...</option>
              {employees.map(emp => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.full_name} ({emp.employee_id})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Tax Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              data-testid="year-select"
              className="w-full h-11 rounded-xl border border-slate-300 px-4 bg-white"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        
        {!selectedEmployee && (
          <div className="mt-4 flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Please select an employee to download forms. Forms will be generated with employee&apos;s payroll data.
            </p>
          </div>
        )}

        {selectedEmployee && !hasPayslipForSelection && (
          <div
            data-testid="forms-no-payroll-banner"
            className="mt-4 flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-xl p-4"
          >
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              No payroll data found for this employee in {selectedYear}. Generate at least one payslip in{' '}
              <a href="/dashboard/payroll" className="font-semibold underline hover:text-amber-900">Payroll</a>{' '}
              before downloading EA / Form E / PCB2(II) / CP21. Forms require year-to-date earnings to populate the statutory fields.
            </p>
          </div>
        )}
      </div>

      {/* Forms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {forms.filter((form) => isAdmin || !form.companyLevel).map((form, idx) => {
          const Icon = form.icon;
          return (
            <div
              key={idx}
              data-testid={`form-card-${form.code}`}
              className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${form.color} flex items-center justify-center`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-slate-500">{form.required}</span>
                </div>
              </div>

              <h3 className="text-lg font-bold text-slate-900 mb-2">{form.title}</h3>
              <p className="text-sm text-slate-600 mb-4 min-h-[60px]">{form.description}</p>
              
              <div className="flex items-center space-x-2 text-xs text-slate-500 mb-4">
                <Building2 className="h-3 w-3" />
                <span>{form.authority}</span>
              </div>

              <Button
                onClick={() => handleDownloadForm(form.code)}
                disabled={loading || (!form.companyLevel && (!selectedEmployee || (!form.noPayrollNeeded && !hasPayslipForSelection)))}
                data-testid={`download-${form.code}`}
                title={
                  form.companyLevel
                    ? `Download ${form.code} for all employees, ${selectedYear}`
                    : !selectedEmployee
                      ? 'Select an employee first'
                      : (!form.noPayrollNeeded && !hasPayslipForSelection)
                        ? `Generate a payslip for ${selectedYear} to enable this download`
                        : `Download ${form.code}`
                }
                className="w-full bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            </div>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-slate-800 dark:to-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center">
          <AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
          Important Information
        </h3>
        <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>Borang EA:</strong> Must be given to every employee by 28 February of the following year</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>Borang E:</strong> Employer's return (with C.P.8D listing) due to LHDN by 31 March — covers all employees</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>CP22 / CP22A / CP21:</strong> Joiner, leaver and leaving-Malaysia notifications to LHDN; CP22 only needs employee details, no payslip</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>Monthly KWSP Borang A &amp; PERKESO Borang 8A</strong> plus the KWSP / PERKESO / LHDN upload .txt files are in Payroll → Contribution Reports</p>
          </div>
          <div className="flex items-start space-x-2">
            <ChevronRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p><strong>Employer numbers</strong> (E no., KWSP, PERKESO, SSM) printed on every form come from Settings → Company Settings</p>
          </div>
        </div>
      </div>
    </div>
  );
}